import React, { memo } from "react";
import Image from "@Components/Image";
import BreadCrumbs from "@Components/BreadCrumbs";
import SocialIcons from "@Pages/Blog/BlogPost/components/SocialIcons/SocialIcons";
// import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import styles from "./ContentPostLayout.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";
import useIsDesktop from "hooks/useIsDesktop";
import CustomLink from "@Components/CustomLink";
import useTranslationHook from "hooks/useTranslationHook";

type TocItem = { id?: string; text: string; level?: number | string };

type Props = {
  postData: any;
  articleContent: React.ReactNode[];
  tableOfContents: TocItem[];
  formatDate: (dateString: string) => string;
  basePath: string;
  breadcrumbLabel: string;
  sidebarContent: React.ReactNode;
  faqContent?: React.ReactNode;
  dateLabel?: string;
};

const ContentPostLayout: React.FC<Props> = ({
  postData,
  articleContent,
  tableOfContents,
  formatDate,
  basePath,
  breadcrumbLabel,
  sidebarContent,
  faqContent,
  dateLabel = "Last updated on ",
}) => {
  const isDesktop = useIsDesktop();
  const { t } = useTranslationHook();
  return (
    <div className={styles.postContainer}>
      <div className={styles.breadcrumbsContainer}>
        <BreadCrumbs
          data={[
            { link: "/", name: t("blog.grid.breadcrumb.home") },
            { link: basePath, name: t(breadcrumbLabel) },
            { link: `${basePath}/post/${postData.slug}`, name: postData.name },
          ]}
          truncateLastItem
        />
      </div>
      <div className={styles.contentWrapper}>
        {/* Main Content Column */}
        <div className={styles.mainColumn}>
          {/* Hero Image */}
          <div className={styles.heroImage}>
            <Image
              src={`${postData.mainImage.url}?w=${isDesktop ? 1000 : 500}`}
              alt={postData.mainImage.alt}
              width="944"
              height="388"
              isEagerLoad
            />
          </div>

          {/* Article Header */}
          <div className={styles.articleHeader}>
            {/* Meta Info */}
            <div className={styles.metaInfo}>
              {postData.category && (
                <CustomLink
                  href={`${basePath}/category/${postData.category.slug}`}
                  className={styles.badge}
                >
                  {postData.category.name}
                </CustomLink>
              )}
              <div className={styles.metaDetails}>
                {postData.readTime && <span>{postData.readTime}</span>}
                {postData.readTime && (postData.updatedAt || postData.date) && (
                  <span className={styles.separator}>·</span>
                )}
                {(postData.updatedAt || postData.date) && (
                  <span>
                    {t(dateLabel)}
                    {formatDate(postData.updatedAt || postData.date)}
                  </span>
                )}
              </div>
            </div>

            {/* Title */}
            <div className={styles.articleTitleSection}>
              <h1 className={styles.articleTitle}>{postData.name}</h1>
              {postData.taglines && <p className={styles.articleTagline}>{postData.taglines}</p>}
            </div>

            {/* Table of Contents */}
            {tableOfContents.length > 0 && (
              <div className={styles.tableOfContents}>
                <h3 className={styles.tocTitle}>{t("Table of Content")}</h3>
                <ol className={styles.tocList}>
                  {tableOfContents.map((item, index) => {
                    const levelClass = item.level
                      ? (styles as any)[`tocItem--${item.level}`]
                      : undefined;

                    return (
                      <li key={index} className={levelClass}>
                        {item.id ? (
                          <CustomLink href={`#${item.id}`} className={styles.tocLink}>
                            {item.text}
                          </CustomLink>
                        ) : (
                          item.text
                        )}
                      </li>
                    );
                  })}
                </ol>
              </div>
            )}

            {/* Post Body */}
            <div className={styles.articleContent}>
              {articleContent}

              {/* Tags */}
              <div className={styles.postBodyBottom}>
                {postData.tags && postData.tags.length > 0 && (
                  <div>
                    <div className={styles.tags}>
                      {postData.tags.map((tag: any, index: number) => (
                        <CustomLink
                          key={index}
                          href={`${basePath}/tag/${tag.slug}`}
                          className={styles.tag}
                        >
                          #{tag.name}
                        </CustomLink>
                      ))}
                    </div>
                  </div>
                )}
                <SocialIcons />
              </div>
            </div>
          </div>
          {!isDesktop && faqContent && <div className={styles.horizontalDivider} />}

          {/* FAQ Section (optional) */}
          {faqContent}
        </div>

        {/* Vertical Divider */}
        <div className={styles.verticalDivider} />

        {/* Sidebar Column */}
        {sidebarContent}
      </div>
    </div>
  );
};

export default wrapperHOC(memo(ContentPostLayout), {
  componentName: "ContentPostLayout",
  showForChina: true,
});
